import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { LogOut, Plus, Calendar as CalIcon, ArrowRight, Trash2, Archive, Users, ClipboardList, Tv, FolderCog } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import ProgramSwitcher from "@/components/ProgramSwitcher";
import { BRAND_WORDMARK_HORIZONTAL } from "@/lib/brand";

const toYMD = (d) => format(d, "yyyy-MM-dd");

export default function CalendarPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [date, setDate] = useState(new Date());
  const [episodes, setEpisodes] = useState([]);
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [rEp, rB] = await Promise.all([api.get("/episodes"), api.get("/production")]);
    setEpisodes(rEp.data);
    setBoards(rB.data);
  };
  useEffect(() => {
    load();
  }, []);

  const openEspelhoForDate = async () => {
    setLoading(true);
    try {
      const ymd = toYMD(date);
      const existing = episodes.find((e) => e.date === ymd);
      if (existing) {
        nav(`/espelho/${existing.id}`);
        return;
      }
      if (!window.confirm(`Não existe espelho de edição para ${format(date, "PPP", { locale: ptBR })}. Deseja criar?`)) return;
      const r = await api.post("/episodes", { date: ymd, title: "" });
      toast.success("Espelho de edição criado.");
      nav(`/espelho/${r.data.id}`);
    } catch (e) {
      toast.error("Não consegui abrir o espelho.");
    } finally {
      setLoading(false);
    }
  };

  const openProducaoForDate = async () => {
    setLoading(true);
    try {
      const ymd = toYMD(date);
      const existing = boards.find((b) => b.date === ymd);
      if (existing) {
        nav(`/producao/${existing.id}`);
        return;
      }
      if (!window.confirm(`Não existe pauta de produção para ${format(date, "PPP", { locale: ptBR })}. Deseja criar?`)) return;
      const r = await api.post("/production", { date: ymd });
      toast.success("Pauta de produção criada.");
      nav(`/producao/${r.data.id}`);
    } catch (e) {
      toast.error("Não consegui abrir a produção.");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir esta edição?")) return;
    await api.delete(`/episodes/${id}`);
    toast.success("Edição removida.");
    load();
  };

  const datesWithEpisodes = new Set(episodes.map((e) => e.date));
  const datesWithBoards = new Set(boards.map((b) => b.date));

  return (
    <div className="min-h-screen bg-white text-zinc-950">
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={BRAND_WORDMARK_HORIZONTAL}
              alt="PodiUmScript"
              className="h-10 w-auto"
            />
            <div className="hidden lg:block">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#E8431E] font-bold">Editorial OS</div>
              <div className="font-heading text-xs text-zinc-500 tracking-tight">Espelhos · Pautas · Teleprompter</div>
            </div>
            <div className="ml-4 pl-4 border-l border-zinc-200">
              <ProgramSwitcher />
            </div>
          </div>
          <div className="flex items-center gap-4">
            {user?.role === "admin" && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="open-programs-admin"
                  onClick={() => nav("/admin/programs")}
                  className="rounded-sm"
                >
                  <FolderCog className="w-4 h-4 mr-2" /> Programas
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="open-users-admin"
                  onClick={() => nav("/admin/users")}
                  className="rounded-sm"
                >
                  <Users className="w-4 h-4 mr-2" /> Usuários
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              size="sm"
              data-testid="open-gaveta"
              onClick={() => nav("/gaveta")}
              className="rounded-sm"
            >
              <Archive className="w-4 h-4 mr-2" /> Gaveta
            </Button>
            <span className="text-sm text-zinc-600 hidden md:inline" data-testid="user-name">{user?.name || user?.email}</span>
            <Button
              variant="ghost"
              size="sm"
              data-testid="logout-button"
              onClick={async () => { await logout(); nav("/login"); }}
              className="rounded-sm"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Calendar */}
        <section className="lg:col-span-1">
          <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-zinc-500 mb-3">Calendário</div>
          <Card className="rounded-sm border-zinc-200 shadow-none p-4">
            <Calendar
              mode="single"
              selected={date}
              onSelect={(d) => d && setDate(d)}
              locale={ptBR}
              modifiers={{
                hasEp: (d) => datesWithEpisodes.has(toYMD(d)),
                hasBoard: (d) => datesWithBoards.has(toYMD(d)),
                hasBoth: (d) => datesWithEpisodes.has(toYMD(d)) && datesWithBoards.has(toYMD(d)),
              }}
              modifiersClassNames={{
                hasEp: "relative font-bold text-[#152D4B]",
                hasBoard: "relative font-bold text-amber-600",
                hasBoth: "relative font-bold text-[#152D4B]",
              }}
              className="rounded-sm"
            />
          </Card>

          <div className="mt-3 flex items-center gap-3 text-[10px] uppercase tracking-wider text-zinc-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 bg-[#152D4B]" /> Edição
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 bg-amber-500" /> Produção
            </span>
          </div>

          <div className="mt-4 space-y-2">
            <div className="text-sm text-zinc-700">
              Selecionado: <span className="font-semibold">{format(date, "PPPP", { locale: ptBR })}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                data-testid="open-espelho-for-date"
                onClick={openEspelhoForDate}
                disabled={loading}
                className="h-11 rounded-sm bg-[#152D4B] hover:bg-[#0E1F36] text-white"
              >
                <Tv className="w-4 h-4 mr-2" /> Espelho de edição
              </Button>
              <Button
                data-testid="open-producao-for-date"
                onClick={openProducaoForDate}
                disabled={loading}
                variant="outline"
                className="h-11 rounded-sm border-amber-500 text-amber-700 hover:bg-amber-50"
              >
                <ClipboardList className="w-4 h-4 mr-2" /> Produção
              </Button>
            </div>
          </div>
        </section>

        {/* Episodes list */}
        <section className="lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-zinc-500">Edições</div>
            <div className="text-xs text-zinc-500">{episodes.length} total</div>
          </div>
          {episodes.length === 0 ? (
            <Card className="rounded-sm border-dashed border-zinc-300 shadow-none p-12 text-center">
              <CalIcon className="w-8 h-8 text-zinc-400 mx-auto mb-3" />
              <div className="font-heading text-2xl font-bold tracking-tight">Nenhuma edição ainda</div>
              <p className="text-zinc-500 mt-2 max-w-md mx-auto">
                Escolha uma data no calendário e crie o primeiro espelho do programa.
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {episodes.map((ep) => (
                <Card
                  key={ep.id}
                  data-testid={`episode-card-${ep.date}`}
                  className="group rounded-sm border-zinc-200 shadow-none p-5 hover:border-zinc-900 hover:-translate-y-[1px] transition-all cursor-pointer"
                  onClick={() => nav(`/espelho/${ep.id}`)}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-zinc-500">
                        {format(parseISO(ep.date), "EEEE", { locale: ptBR })}
                      </div>
                      <div className="font-heading text-2xl font-bold tracking-tight mt-1">
                        {format(parseISO(ep.date), "dd 'de' MMMM", { locale: ptBR })}
                      </div>
                      <div className="text-sm text-zinc-600 mt-1">{ep.title}</div>
                      {ep.subtitle && (
                        <div className="text-xs text-[#152D4B] font-semibold mt-1 italic">
                          {ep.subtitle}
                        </div>
                      )}
                    </div>
                    <ArrowRight className="w-5 h-5 text-zinc-400 group-hover:text-[#152D4B] group-hover:translate-x-0.5 transition" />
                  </div>
                  <div className="mt-4 flex items-center justify-between border-t border-zinc-100 pt-3">
                    <div className="text-xs text-zinc-500">
                      {ep.retrancas.length} retranca{ep.retrancas.length !== 1 ? "s" : ""}
                    </div>
                    <button
                      data-testid={`delete-episode-${ep.id}`}
                      onClick={(e) => { e.stopPropagation(); remove(ep.id); }}
                      className="text-xs text-zinc-400 hover:text-red-600 flex items-center gap-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Excluir
                    </button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
